import {
  Camera,
  FileText,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Mic,
  PackageCheck,
  Plus,
  SendHorizontal,
  Smile,
  X,
} from "lucide-react";
import {
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
  type Ref,
} from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { toast } from "sonner";

import type {
  ChatMessageDto,
  DeliveryStatus,
  LocationInput,
  MediaKind,
} from "@mandoob/shared";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { errorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";

import { AudioRecorder } from "./audio-recorder";
import { EmojiPicker } from "./emoji-picker";
import { firstUrl, LinkPreview } from "./link-preview";
import { quotedPreview } from "./message-bubble";
import { CameraDialog } from "./camera-dialog";
import { LocationDialog } from "./location-dialog";
import {
  formatBytes,
  MEDIA_MAX_BYTES,
  mediaKindForMime,
  prepareImageForUpload,
  uploadMedia,
} from "./media";
import {
  convertVideoToMp4,
  isMp4File,
  isVideoFile,
  VIDEO_MAX_BYTES,
  VIDEO_TARGET_BYTES,
} from "./video-transcode";

// Media-capable composer for the chat thread, ported from the reference
// chat-composer.tsx and trimmed to Mandoob's feature set (no quick responses,
// emoji picker, replies or link previews).

// WhatsApp caps a text message body at 4096 characters.
const MESSAGE_MAX_LENGTH = 4096;

const DOCUMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,application/pdf,text/plain";

/** What the composer hands the thread page to merge with conversationId. */
export interface ComposerSendPayload {
  body?: string;
  mediaId?: string;
  mediaKind?: MediaKind;
  mediaFileName?: string;
  location?: LocationInput;
}

interface ComposerAttachment {
  file: File;
  kind: MediaKind;
  /** Object URL thumbnail for images, null otherwise. */
  previewUrl: string | null;
}

export interface ChatComposerHandle {
  /** Puts the caret in the message textarea. */
  focus: () => void;
}

export function ChatComposer({
  onSend,
  replyTo,
  replyToName,
  onCancelReply,
  onReplyClick,
  deliveryStatus,
  onMarkDelivered,
  countdown,
  ref,
}: {
  /**
   * Dispatches the payload (the parent adds conversationId, appends the
   * optimistic pending bubble, and owns the mutation's error toasts).
   */
  onSend: (payload: ComposerSendPayload) => void;
  /** Quoted-reply preview shown inside the pill (reference layout). */
  replyTo?: ChatMessageDto | null;
  /** Client display name for an inbound quoted message. */
  replyToName?: string;
  onCancelReply?: () => void;
  /** Tapping the reply preview jumps the thread to the quoted message. */
  onReplyClick?: () => void;
  /** Drives which quick actions the top bar shows. */
  deliveryStatus?: DeliveryStatus;
  /** Quick action: mark this delivery Delivered (shown while undelivered). */
  onMarkDelivered?: () => void;
  /** Window countdown chip rendered at the end of the quick-actions bar. */
  countdown?: React.ReactNode;
  ref?: Ref<ChatComposerHandle>;
}) {
  const { t, dir } = useLanguage();
  // A "New" delivery has no client response yet — no actions to offer.
  const showQuickActions = deliveryStatus !== "New";
  const [value, setValue] = useState("");
  const [attachment, setAttachment] = useState<ComposerAttachment | null>(null);
  // Non-null while preparing a picked file (image re-encode, video transcode);
  // holds the localized label shown in the chip.
  const [processing, setProcessing] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  // URL whose draft preview the driver closed — stays hidden until it changes.
  const [dismissedUrl, setDismissedUrl] = useState<string | null>(null);

  const mediaInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
  }));

  /** Inserts a picked emoji at the caret and hands focus back to the input. */
  const insertEmoji = (emoji: string) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = (value.slice(0, start) + emoji + value.slice(end)).slice(
      0,
      MESSAGE_MAX_LENGTH,
    );
    setValue(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = Math.min(start + emoji.length, next.length);
      el.setSelectionRange(caret, caret);
    });
  };

  const busy = uploading || processing !== null;

  const clearAttachment = () => {
    setAttachment((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  };

  /** Prepares a picked/pasted/captured file and stages it as the attachment. */
  // The camera option opens the phone's real camera app on native (same flow
  // as the shipping-label capture); the in-page getUserMedia dialog is the
  // web-only fallback.
  const openCamera = async () => {
    if (!isNative()) {
      setCameraOpen(true);
      return;
    }
    try {
      const { Camera, CameraResultType, CameraSource } =
        await import("@capacitor/camera");
      const photo = await Camera.getPhoto({
        quality: 90,
        source: CameraSource.Camera,
        resultType: CameraResultType.Uri,
        correctOrientation: true,
      });
      if (!photo.webPath) return;
      const blob = await fetch(photo.webPath).then((res) => res.blob());
      const file = new File([blob], `photo-${Date.now()}.jpg`, {
        type: blob.type || "image/jpeg",
      });
      void pickFile(file);
    } catch {
      // Camera dismissed — nothing to do.
    }
  };

  const pickFile = async (file: File) => {
    if (processing !== null || uploading) return;
    const isImage = file.type.toLowerCase().startsWith("image/");
    const isVideo = isVideoFile(file);

    if (isVideo && file.size > VIDEO_MAX_BYTES) {
      toast.error(
        t(
          "Mandoob:Composer.FileTooLarge",
          formatBytes(file.size),
          formatBytes(VIDEO_MAX_BYTES),
        ),
      );
      return;
    }
    if (!isImage && !isVideo && file.size > MEDIA_MAX_BYTES) {
      toast.error(
        t(
          "Mandoob:Composer.FileTooLarge",
          formatBytes(file.size),
          formatBytes(MEDIA_MAX_BYTES),
        ),
      );
      return;
    }

    try {
      let prepared = file;
      if (isImage) {
        setProcessing(t("Mandoob:Composer.Compressing"));
        prepared = await prepareImageForUpload(file);
      } else if (
        isVideo &&
        !(isMp4File(file) && file.size <= VIDEO_TARGET_BYTES)
      ) {
        // Convert every video to MP4/H.264, compressing it under the send
        // limit. An already-small MP4 is left untouched.
        const converting = t("Mandoob:Composer.ConvertingVideo");
        setProcessing(converting);
        const result = await convertVideoToMp4(file, (fraction) =>
          setProcessing(`${converting} ${Math.round(fraction * 100)}%`),
        );
        if (result.ok) {
          prepared = result.file;
        } else {
          // Don't fall back to uploading the raw file: a non-MP4 (or
          // oversized) source WhatsApp can't accept would only fail on send.
          toast.error(
            result.reason === "unsupported"
              ? t("Mandoob:Composer.VideoUnsupported")
              : result.reason === "toobig"
                ? t(
                    "Mandoob:Composer.VideoTooBig",
                    formatBytes(VIDEO_TARGET_BYTES),
                  )
                : t("Mandoob:Composer.VideoProcessFailed"),
          );
          return;
        }
      }

      // Post-processing guard — the upload endpoint rejects anything larger.
      if (prepared.size > MEDIA_MAX_BYTES) {
        toast.error(
          t(
            "Mandoob:Composer.FileTooLarge",
            formatBytes(prepared.size),
            formatBytes(MEDIA_MAX_BYTES),
          ),
        );
        return;
      }

      clearAttachment();
      setAttachment({
        file: prepared,
        kind: mediaKindForMime(prepared.type),
        previewUrl: prepared.type.startsWith("image/")
          ? URL.createObjectURL(prepared)
          : null,
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleFileInput = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await pickFile(file);
  };

  const handlePaste = async (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const file = event.clipboardData.files[0];
    if (!file) return;
    event.preventDefault();
    await pickFile(file);
  };

  /**
   * Uploads a prepared file, then dispatches the send (fire-and-forget — the
   * thread shows an optimistic bubble with the pending clock, WhatsApp-style).
   */
  const uploadAndSend = async (
    file: File,
    kind: MediaKind,
    caption: string | undefined,
  ): Promise<boolean> => {
    setUploading(true);
    try {
      let uploaded;
      try {
        uploaded = await uploadMedia(file, file.name);
      } catch (error) {
        toast.error(errorMessage(error, t));
        return false;
      }
      onSend({
        mediaId: uploaded.mediaId,
        mediaKind: kind,
        mediaFileName: file.name,
        body: caption,
      });
      return true;
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (busy) return;
    const caption = value.trim();
    if (attachment) {
      const ok = await uploadAndSend(
        attachment.file,
        attachment.kind,
        caption || undefined,
      );
      if (ok) {
        clearAttachment();
        setValue("");
      }
      return;
    }
    if (!caption) return;
    // Optimistic: the parent appends a pending bubble immediately; the input
    // clears right away rather than waiting on the server.
    onSend({ body: caption });
    setValue("");
  };

  const sendAudio = async (file: File) => {
    setRecording(false);
    await uploadAndSend(file, "Audio", undefined);
  };

  const sendLocation = (location: LocationInput) => {
    onSend({ location });
  };

  const showSend = !!value.trim() || !!attachment;

  // Live link preview for the first URL in the draft (debounced so the card
  // doesn't flicker while typing). Dismissable per URL; a different URL in the
  // draft brings the preview back.
  const debouncedDraft = useDebounce(value, 400);
  const draftUrl = !attachment && !recording ? firstUrl(debouncedDraft) : null;
  const showDraftPreview = !!draftUrl && draftUrl !== dismissedUrl;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {attachment || processing !== null || uploading ? (
        <div className="tw:flex tw:items-center tw:gap-2.5 tw:rounded-xl tw:border tw:border-border tw:bg-background tw:px-3 tw:py-2">
          {processing !== null || uploading ? (
            <Loader2
              className="tw:size-4 tw:shrink-0 tw:animate-spin tw:text-muted-foreground"
              strokeWidth={1.75}
            />
          ) : attachment?.previewUrl ? (
            <img
              src={attachment.previewUrl}
              alt=""
              className="tw:size-9 tw:shrink-0 tw:rounded tw:object-cover"
            />
          ) : (
            <FileText
              className="tw:size-5 tw:shrink-0 tw:text-muted-foreground"
              strokeWidth={1.75}
            />
          )}
          <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:text-foreground">
            {processing !== null
              ? processing
              : uploading
                ? t("Mandoob:Composer.Uploading")
                : attachment?.file.name}
          </span>
          {attachment && processing === null && !uploading ? (
            <button
              type="button"
              aria-label={t("Mandoob:Composer.RemoveAttachment")}
              onClick={clearAttachment}
              className="tw:shrink-0 tw:rounded-md tw:p-1 tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted tw:hover:text-foreground"
            >
              <X className="tw:size-4" strokeWidth={1.75} />
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Quick-actions bar attached to the pill's top: the shortcuts a driver
          reaches for on a delivery (photo proof, share location, mark
          delivered while undelivered). A "New" delivery shows no actions —
          nothing to act on until the client responds. The window countdown
          lives inside the pill itself, at the end of the text field. */}
      {showQuickActions ? (
        <div className="tw:-mb-6 tw:flex tw:items-center tw:gap-1.5 tw:overflow-x-auto tw:rounded-t-[1.75rem] tw:border tw:border-b-0 tw:border-input tw:bg-background tw:px-3 tw:pb-6 tw:pt-1.5 tw:[scrollbar-width:none] tw:dark:bg-card">
          {onMarkDelivered &&
          deliveryStatus !== "Delivered" &&
          deliveryStatus !== "Cancelled" ? (
            <button
              type="button"
              onClick={onMarkDelivered}
              className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-success-soft tw:px-2.5 tw:py-1 tw:text-xs tw:font-semibold tw:text-success-foreground tw:transition-transform tw:active:scale-95"
            >
              <PackageCheck className="tw:size-3.5" strokeWidth={2} />
              {t("Mandoob:Composer.QuickDelivered")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void openCamera()}
            disabled={busy}
            className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-muted tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-foreground tw:transition-transform tw:active:scale-95"
          >
            <Camera className="tw:size-3.5" strokeWidth={2} />
            {t("Mandoob:Camera.Capture")}
          </button>
          <button
            type="button"
            onClick={() => setLocationOpen(true)}
            disabled={busy}
            className="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:whitespace-nowrap tw:rounded-full tw:bg-muted tw:px-2.5 tw:py-1 tw:text-xs tw:font-medium tw:text-foreground tw:transition-transform tw:active:scale-95"
          >
            <MapPin className="tw:size-3.5" strokeWidth={2} />
            {t("Mandoob:Composer.SendCurrentLocation")}
          </button>
        </div>
      ) : null}

      {/* Reference layout: ONE pill contains the reply preview, the link
          preview and the controls row (recorder swaps into the same row). */}
      <div className="tw:relative tw:flex tw:flex-col tw:rounded-4xl tw:border tw:border-input tw:bg-background tw:p-2 tw:ps-3 tw:dark:bg-card">
        {replyTo ? (
          // Tapping the preview scrolls the thread to the quoted message
          // (same jump the in-bubble quote does); the X keeps its own job.
          <div
            onClick={onReplyClick}
            className={cn(
              "tw:mb-2 tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:rounded-2xl tw:border-s-4 tw:bg-muted tw:px-3 tw:py-2",
              replyTo.direction === "Outbound"
                ? "tw:border-s-brand"
                : "tw:border-s-[#007BFC]",
            )}
          >
            <div className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5">
              <span
                className={cn(
                  "tw:truncate tw:text-xs tw:font-semibold",
                  replyTo.direction === "Outbound"
                    ? "tw:text-brand"
                    : "tw:text-[#007BFC]",
                )}
              >
                {replyTo.direction === "Outbound"
                  ? t("Mandoob:Message.You")
                  : replyToName}
              </span>
              <span className="tw:line-clamp-2 tw:whitespace-pre-wrap tw:break-words tw:text-sm tw:text-muted-foreground">
                {quotedPreview(replyTo, t)}
              </span>
            </div>
            <button
              type="button"
              aria-label={t("Mandoob:Message.CancelReply")}
              onClick={(event) => {
                event.stopPropagation();
                onCancelReply?.();
              }}
              className="tw:shrink-0 tw:rounded-md tw:p-1 tw:text-muted-foreground tw:transition-colors tw:hover:bg-background tw:hover:text-foreground"
            >
              <X className="tw:size-4" strokeWidth={1.75} />
            </button>
          </div>
        ) : null}

        {showDraftPreview ? (
          <div className="tw:relative tw:mb-2">
            <div className="tw:-mb-1.5">
              <LinkPreview url={draftUrl} />
            </div>
            <button
              type="button"
              aria-label={t("Close")}
              onClick={() => setDismissedUrl(draftUrl)}
              className="tw:absolute tw:end-2 tw:top-1/2 tw:-translate-y-1/2 tw:rounded-full tw:p-1 tw:text-muted-foreground tw:transition-colors tw:hover:bg-black/5 tw:hover:text-foreground"
            >
              <X className="tw:size-4" strokeWidth={1.75} />
            </button>
          </div>
        ) : null}

        <div className="tw:flex tw:items-center tw:gap-1">
          {recording ? (
            <AudioRecorder
              onFinish={sendAudio}
              onCancel={() => setRecording(false)}
            />
          ) : (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t("Mandoob:Composer.Attach")}
                    disabled={busy}
                    className={cn(
                      // Slightly smaller and tighter on phones; desktop keeps
                      // the reference sizing.
                      "tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-foreground tw:transition-colors tw:hover:bg-muted tw:md:size-11",
                      busy && "tw:pointer-events-none tw:opacity-50",
                    )}
                  >
                    <Plus
                      className="tw:size-6 tw:md:size-7"
                      strokeWidth={1.75}
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  side="top"
                  className="tw:w-52"
                >
                  <DropdownMenuItem onSelect={() => void openCamera()}>
                    <Camera className="tw:size-4" strokeWidth={1.75} />
                    {t("Mandoob:Composer.Camera")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => mediaInputRef.current?.click()}
                  >
                    <ImageIcon className="tw:size-4" strokeWidth={1.75} />
                    {t("Mandoob:Composer.PhotoVideo")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => documentInputRef.current?.click()}
                  >
                    <FileText className="tw:size-4" strokeWidth={1.75} />
                    {t("Mandoob:Composer.Document")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setLocationOpen(true)}>
                    <MapPin className="tw:size-4" strokeWidth={1.75} />
                    {t("Mandoob:Composer.Location")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <EmojiPicker onSelect={insertEmoji}>
                <button
                  type="button"
                  aria-label={t("Mandoob:Composer.Emoji")}
                  disabled={busy}
                  className={cn(
                    "tw:-ms-3 tw:flex tw:size-9 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:text-foreground tw:transition-colors tw:hover:bg-muted tw:md:-ms-2 tw:md:size-11",
                    busy && "tw:pointer-events-none tw:opacity-50",
                  )}
                >
                  <Smile
                    className="tw:size-5.5 tw:md:size-6.5"
                    strokeWidth={1.75}
                  />
                </button>
              </EmojiPicker>

              <input
                ref={mediaInputRef}
                type="file"
                accept="image/*,video/*"
                onChange={(event) => void handleFileInput(event)}
                className="tw:hidden"
              />
              <input
                ref={documentInputRef}
                type="file"
                accept={DOCUMENT_ACCEPT}
                onChange={(event) => void handleFileInput(event)}
                className="tw:hidden"
              />

              <textarea
                ref={textareaRef}
                value={value}
                rows={1}
                maxLength={MESSAGE_MAX_LENGTH}
                onChange={(event) =>
                  setValue(event.target.value.slice(0, MESSAGE_MAX_LENGTH))
                }
                onPaste={(event) => void handlePaste(event)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void submit();
                  }
                }}
                // App-language direction while empty (placeholder and caret
                // sit on the natural side); content-driven once typing starts,
                // like the reference's detectDir.
                dir={value ? "auto" : dir}
                placeholder={
                  attachment
                    ? t("Mandoob:Composer.CaptionPlaceholder")
                    : t("Mandoob:Thread.ComposerPlaceholder")
                }
                // Tighter against the emoji button on phones; desktop keeps
                // the reference spacing.
                className="tw:field-sizing-content tw:-ms-1.5 tw:max-h-40 tw:min-h-7 tw:min-w-0 tw:flex-1 tw:resize-none tw:self-center tw:bg-transparent tw:px-1 tw:py-1 tw:text-base tw:text-foreground tw:outline-none tw:placeholder:text-muted-foreground tw:md:ms-0"
              />

              {/* Window countdown at the end of the field — steps aside as
                  soon as the driver starts typing. */}
              {countdown && !value && !attachment ? (
                <span className="tw:shrink-0 tw:self-center">{countdown}</span>
              ) : null}

              {showSend ? (
                <Button
                  type="button"
                  size="icon"
                  onClick={() => void submit()}
                  disabled={busy}
                  aria-label={t("Mandoob:Thread.Send")}
                  className="tw:size-12 tw:shrink-0 tw:rounded-full"
                >
                  {busy ? (
                    <Loader2 className="tw:size-6 tw:animate-spin" />
                  ) : (
                    <SendHorizontal
                      className="tw:size-6 tw:rtl:-scale-x-100"
                      strokeWidth={1.75}
                    />
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  size="icon"
                  onClick={() => setRecording(true)}
                  disabled={busy}
                  aria-label={t("Mandoob:Composer.Record")}
                  className="tw:size-12 tw:shrink-0 tw:rounded-full tw:bg-transparent tw:text-foreground tw:shadow-none tw:transition-none tw:hover:bg-brand tw:hover:text-brand-foreground"
                >
                  <Mic className="tw:size-6" strokeWidth={2} />
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <CameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => void pickFile(file)}
      />
      <LocationDialog
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSend={sendLocation}
      />
    </div>
  );
}
